import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Bell, BellOff, ChefHat, Expand, Martini, Printer, RotateCcw, Shrink } from 'lucide-react';
import type { Permission } from '@garlic/shared';
import { api, errorMessage, post } from '../../lib/api';
import { beep, useNow, type ChangeEvent } from '../../lib/realtime';
import { Alert, Badge, Button, Card, EmptyState, PageHeader, Spinner, cx } from '../../ui/primitives';
import { useToast } from '../../ui/feedback';
import { staffPath } from '../../lib/paths';

type PrintState = { printers: number; online: number; failedRecent: number; pending: number; state: 'none' | 'offline' | 'failures' | 'ok' };
type Station = { id: string; name: string; kind: 'KITCHEN' | 'BAR' | 'OTHER'; mode: 'PRINTER' | 'SCREEN' | 'BOTH'; canOperate: boolean; activeTickets: number; print: PrintState };
type Ticket = {
  id: string; status: 'RECEIVED' | 'IN_PREPARATION' | 'READY' | 'DELIVERED'; createdAt: string; startedAt: string | null; readyAt: string | null; deliveredAt: string | null;
  expectedMinutes: number; orderNumber: number; tableLabel: string; customerName: string | null; orderNotes: string | null; byStaff: boolean;
  lastPrint: { status: string; lastError: string | null; kind: string } | null;
  items: { id: string; quantity: number; productName: string; notes: string | null; status: string; options: string[] }[];
};
type Queue = { station: { id: string; name: string; kind: Station['kind'] }; print: PrintState; serverTime: string; tickets: Ticket[] };

const stationIcon = (kind: Station['kind']) => (kind === 'BAR' ? Martini : ChefHat);

export function StationPicker() {
  const q = useQuery({ queryKey: ['staff-stations'], queryFn: () => api<Station[]>('/api/staff/stations') });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  // Setor só com impressora aparece aqui apenas como reserva, quando a impressora dele está com problema.
  const fallback = (s: Station) => s.mode === 'PRINTER' && (s.print.state !== 'ok' || s.activeTickets > 0);
  const mine = q.data.filter((s) => s.canOperate && (s.mode !== 'PRINTER' || fallback(s)));
  return (
    <>
      <PageHeader title="Produção" description="Escolha o setor (cozinha, bar…). A tela atualiza em tempo real e pode ficar aberta num tablet ou TV." />
      {mine.length === 0 ? (
        <Card>
          {q.data.some((s) => s.canOperate) ? (
            <EmptyState title="Nenhum setor usa tela">Os setores estão como “só impressora”. Para usar telas, mude em Configurações → Setores de produção.</EmptyState>
          ) : (
            <EmptyState title="Você não opera nenhum setor" />
          )}
        </Card>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {mine.map((s) => {
            const Icon = stationIcon(s.kind);
            return (
              <li key={s.id}>
                <Link to={staffPath(`/producao/${s.id}`)} className="flex items-center gap-4 rounded-2xl border border-line bg-white p-5 shadow-card hover:bg-paper">
                  <span className="grid size-12 place-items-center rounded-2xl bg-garlic-50 text-garlic-600"><Icon className="size-6" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display text-xl font-semibold">{s.name}</span>
                    <span className="block text-sm text-ink-3">{s.activeTickets} comanda(s) em andamento</span>
                    {s.mode === 'PRINTER' && <span className="mt-1 block text-xs font-medium text-warn-600">Reserva: impressora com problema</span>}
                  </span>
                  <PrintBadge print={s.print} />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function PrintBadge({ print }: { print: PrintState }) {
  if (print.state === 'none') return <Badge>Sem impressora</Badge>;
  if (print.state === 'offline') return <Badge tone="danger" dot>Impressão offline</Badge>;
  if (print.state === 'failures') return <Badge tone="warning" dot>{print.failedRecent} falha(s)</Badge>;
  return <Badge tone="success" dot>Impressão ok</Badge>;
}

const COLUMNS: { status: Ticket['status']; label: string; next?: Ticket['status']; action?: string }[] = [
  { status: 'RECEIVED', label: 'Novos', next: 'IN_PREPARATION', action: 'Iniciar preparo' },
  { status: 'IN_PREPARATION', label: 'Em preparo', next: 'READY', action: 'Pronto' },
  { status: 'READY', label: 'Prontos', next: 'DELIVERED', action: 'Entregue' },
];

/** Tela do setor: colunas por status, cronômetro, atraso destacado, observações bem visíveis. */
export function StationScreen({ permissions }: { permissions: Permission[] }) {
  const { stationId = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [showDelivered, setShowDelivered] = useState(false);
  const [sound, setSound] = useState(() => localStorage.getItem('garlic:sound') === '1');
  const [full, setFull] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const known = useRef<Set<string> | null>(null);
  const now = useNow(1000);
  const q = useQuery({
    queryKey: ['staff-station', stationId, showDelivered],
    queryFn: () => api<Queue>(`/api/staff/stations/${stationId}/tickets?delivered=${showDelivered ? 1 : 0}`),
    refetchInterval: 30_000, // rede de segurança; o normal é chegar por SSE
  });

  // Som ao chegar comanda nova (compara ids entre atualizações).
  useEffect(() => {
    if (!q.data) return;
    const ids = new Set(q.data.tickets.filter((t) => t.status === 'RECEIVED').map((t) => t.id));
    if (known.current && sound && [...ids].some((id) => !known.current!.has(id))) beep();
    known.current = ids;
  }, [q.data, sound]);
  useEffect(() => {
    const onChange = (e: Event) => {
      const d = (e as CustomEvent<ChangeEvent>).detail;
      if (d.t === 'ticket' && d.st === stationId) void qc.invalidateQueries({ queryKey: ['staff-station', stationId] });
    };
    window.addEventListener('garlic:change', onChange);
    return () => window.removeEventListener('garlic:change', onChange);
  }, [qc, stationId]);

  const toggleFull = async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      /* navegador sem suporte */
    }
    setFull(!!document.fullscreenElement);
  };

  const move = async (t: Ticket, status: Ticket['status']) => {
    setBusy(t.id);
    try {
      await post(`/api/staff/tickets/${t.id}/status`, { status });
      await qc.invalidateQueries({ queryKey: ['staff-station', stationId] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  const reprint = async (t: Ticket) => {
    try {
      await post(`/api/staff/tickets/${t.id}/reprint`);
      toast.success(`Comanda #${String(t.orderNumber).padStart(4, '0')} enviada para impressão`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (q.isPending) return <Spinner />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  const { station, print, tickets } = q.data;
  const offset = new Date(q.data.serverTime).getTime() - Date.now(); // corrige relógio do tablet
  const canReprint = permissions.includes('printing.reprint') && print.printers > 0;

  return (
    <div className={cx(full && 'fixed inset-0 z-40 overflow-auto bg-paper p-4')}>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Link to={staffPath('/producao')} className="text-ink-3 hover:text-ink" aria-label="Voltar"><ArrowLeft className="size-5" /></Link>
        <h1 className="font-display text-2xl font-semibold">{station.name}</h1>
        <PrintBadge print={print} />
        <div className="ml-auto flex flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 rounded-xl border border-line bg-white px-3 text-sm">
            <input type="checkbox" checked={showDelivered} onChange={(e) => setShowDelivered(e.target.checked)} className="accent-garlic-600" /> Entregues (30 min)
          </label>
          <Button
            variant="secondary"
            size="sm"
            icon={sound ? <Bell className="size-4" /> : <BellOff className="size-4" />}
            onClick={() => {
              const v = !sound;
              setSound(v);
              localStorage.setItem('garlic:sound', v ? '1' : '0');
              if (v) beep();
            }}
          >
            {sound ? 'Som ligado' : 'Ligar som'}
          </Button>
          <Button variant="secondary" size="sm" icon={full ? <Shrink className="size-4" /> : <Expand className="size-4" />} onClick={toggleFull}>{full ? 'Sair' : 'Tela cheia'}</Button>
        </div>
      </div>

      {print.state === 'none' && <div className="mb-4"><Alert tone="info">Nenhuma impressora neste setor: as comandas aparecem só nesta tela.</Alert></div>}
      {print.state === 'offline' && <div className="mb-4"><Alert tone="danger" title="Agente de impressão offline">As comandas continuam chegando aqui. Verifique o computador do agente.</Alert></div>}
      {print.state === 'failures' && <div className="mb-4"><Alert tone="warning" title={`${print.failedRecent} impressão(ões) falharam`}>As comandas estão nesta tela. Reimprima se precisar ou veja os detalhes em Impressão.</Alert></div>}

      <div className={cx('grid gap-4', showDelivered ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}>
        {[...COLUMNS, ...(showDelivered ? [{ status: 'DELIVERED' as const, label: 'Entregues' }] : [])].map((col) => {
          const list = tickets.filter((t) => t.status === col.status);
          return (
            <section key={col.status} aria-label={col.label} className="min-w-0">
              <h2 className="mb-2 flex items-center justify-between text-sm font-semibold uppercase tracking-wide text-ink-3">
                {col.label} <span className="rounded-full bg-paper-2 px-2 py-0.5 text-ink-2">{list.length}</span>
              </h2>
              <ul className="space-y-3">
                {list.map((t) => {
                  const elapsedMin = Math.max(0, (now + offset - new Date(t.createdAt).getTime()) / 60_000);
                  const late = t.status !== 'READY' && t.status !== 'DELIVERED' && elapsedMin > t.expectedMinutes;
                  return (
                    <li key={t.id} className={cx('rounded-2xl border-2 bg-white p-4 shadow-card', late ? 'border-danger-600' : t.status === 'READY' ? 'border-sprout-500' : 'border-line')}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-display text-2xl font-semibold leading-none">Mesa {t.tableLabel}</p>
                          <p className="mt-1 text-sm text-ink-3">#{String(t.orderNumber).padStart(4, '0')}{t.customerName ? ` · ${t.customerName}` : ''}{t.byStaff ? ' · garçom' : ''}</p>
                        </div>
                        <span className={cx('rounded-lg px-2 py-1 font-mono text-lg font-semibold tabular-nums', late ? 'bg-danger-600 text-white' : 'bg-paper-2')}>
                          {Math.floor(elapsedMin)}′
                        </span>
                      </div>
                      {late && <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-danger-600"><AlertTriangle className="size-3.5" /> Atrasado (previsto {t.expectedMinutes} min)</p>}
                      <ul className="mt-3 space-y-2">
                        {t.items.map((i) => (
                          <li key={i.id} className={cx(i.status === 'CANCELLED' && 'text-ink-3 line-through')}>
                            <p className="text-lg font-semibold leading-snug"><span className="tabular-nums">{i.quantity}×</span> {i.productName}{i.status === 'CANCELLED' && <span className="ml-2 text-xs font-bold uppercase text-danger-600 no-underline">cancelado</span>}</p>
                            {i.options.map((o) => <p key={o} className="text-sm text-ink-2">• {o}</p>)}
                            {i.notes && <p className="mt-1 rounded-lg bg-warn-50 px-2 py-1 text-sm font-semibold text-warn-600">OBS: {i.notes}</p>}
                          </li>
                        ))}
                      </ul>
                      {t.orderNotes && <p className="mt-3 rounded-lg bg-warn-50 px-2 py-1 text-sm font-semibold text-warn-600">Pedido: {t.orderNotes}</p>}
                      {t.lastPrint?.status === 'FAILED' && <p className="mt-2 text-xs text-danger-600">Falha na impressão: {t.lastPrint.lastError}</p>}
                      <div className="mt-4 flex gap-2">
                        {col.next && (
                          <Button size="lg" className="flex-1" loading={busy === t.id} onClick={() => move(t, col.next!)}>{col.action}</Button>
                        )}
                        {t.status !== 'RECEIVED' && t.status !== 'DELIVERED' && (
                          <Button size="lg" variant="secondary" aria-label="Voltar um passo" title="Desfazer" icon={<RotateCcw className="size-4" />} onClick={() => move(t, t.status === 'READY' ? 'IN_PREPARATION' : 'RECEIVED')} />
                        )}
                        {canReprint && <Button size="lg" variant="secondary" aria-label="Reimprimir" title="Reimprimir" icon={<Printer className="size-4" />} onClick={() => reprint(t)} />}
                      </div>
                    </li>
                  );
                })}
                {list.length === 0 && <li className="rounded-2xl border border-dashed border-line p-6 text-center text-sm text-ink-3">Nada aqui</li>}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
